import {AutoComplete, Button, Input, Select, Spin} from 'antd'
import type {RefSelectProps} from 'antd/es/select'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
  DatabasePlantRecord,
  instance as plantsService,
  prepareOverwrittenBaseFields,
} from '../../services/PlantsService'
import debounce from 'lodash/debounce'
import {useIntl} from 'react-intl'
import {Language} from '../../services/language'

export type PlantDatabaseSearchFieldProps = {
  onDBPlantSelected: (dbPlant: DatabasePlantRecord) => void
  debounceTimeout?: number
}

const noImageUrl = ''

export function PlantDatabaseSearchField({
  debounceTimeout = 500,
  onDBPlantSelected,
}: PlantDatabaseSearchFieldProps) {
  const fetchRef = useRef(0)
  const selectRef = useRef<RefSelectProps>(null)
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<DatabasePlantRecord[]>([])
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const intl = useIntl()

  const debouncedOptionsLoader = useMemo(() => {
    const loadOptions = async (searchQuery: string) => {
      if (!searchQuery || searchQuery.length < 3) {
        setLoading(false)
        setOptions([])
        return
      }

      fetchRef.current += 1
      const fetchId = fetchRef.current

      const searchResult = await plantsService.searchPlantsDatabase(searchQuery)
      if (fetchId != fetchRef.current) {
        return
      }

      setLoading(false)

      if (!searchResult.success) {
        return
      }

      setOptions(searchResult.data?.data?.records || [])
    }

    return debounce(loadOptions, debounceTimeout)
  }, [debounceTimeout])

  useEffect(() => {
    const frame = requestAnimationFrame(() => selectRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [])

  return (
    <div>
      <Select
        ref={selectRef}
        showSearch
        allowClear
        labelInValue
        filterOption={false}
        onSearch={debouncedOptionsLoader}
        notFoundContent={loading ? <Spin size="small" /> : null}
        style={{width: '100%', height: 'auto'}}
        classNames={{popup: {root: 'plant-search-dropdown'}}}
        // The option list is left open after a pick unless it is driven
        // explicitly. With a mouse that is only untidy, but on a touch device the
        // list covers the confirm button underneath it, so there is no way to
        // finish choosing a base plant.
        open={isDropdownOpen}
        onOpenChange={setDropdownOpen}
        onChange={(value, {option = null}: any = {}) => {
          setDropdownOpen(false)
          setSelectedOptionId(value)
          onDBPlantSelected(option)
        }}
        value={selectedOptionId}
        placeholder={intl.formatMessage({id: 'searchForBasePlant'})}
      >
        {options.map((option) => {
          const fields = prepareOverwrittenBaseFields(
            {},
            option.fields,
            {language: intl.locale as Language},
            true
          )

          return (
            <Select.Option value={option.id} key={option.id} option={option}>
              <div className="plant-search-option">
                <div className="plant-search-option__image">
                  <div>
                    <img src={option.thumbnailUrl || noImageUrl} />
                  </div>
                </div>
                <div className="plant-search-option__fields">
                  <strong>{fields['Общераспространенное название']}</strong>
                  {fields['Другие названия'] && (
                    <small className="other-names wrap">{fields['Другие названия']}</small>
                  )}
                  <em>{fields['Научное (латинское) название']}</em>
                  {fields['Сорт'] && <span className="variety wrap">{fields['Сорт']}</span>}
                  {fields['Уточняющая характеристика'] && (
                    <small>({fields['Уточняющая характеристика']})</small>
                  )}
                </div>
              </div>
            </Select.Option>
          )
        })}
      </Select>
    </div>
  )
}

export function PlantDatabaseSearchFieldWithConfirm(
  props: PlantDatabaseSearchFieldProps & {selectButtonText: string}
) {
  const [selectedPlant, setSelectedPlant] = useState<DatabasePlantRecord | null>(null)

  return (
    <div className="baseplant-search">
      <div className="baseplant-search__field">
        <PlantDatabaseSearchField onDBPlantSelected={setSelectedPlant} />
      </div>
      <Button
        className="baseplant-search__confirm"
        disabled={selectedPlant === null}
        type="primary"
        size="middle"
        onClick={() => {
          if (selectedPlant) {
            props.onDBPlantSelected(selectedPlant)
          }
        }}
      >
        {props.selectButtonText}
      </Button>
    </div>
  )
}
